import { Component, OnInit, inject } from '@angular/core'
import { LaunchAuthRedirectService } from '../../../common/services/launch-auth-redirect.service'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { BaseComponent } from '../../../common/components/base.component'
import { AuthService } from '../../../common/services/auth.service'
import { SpinnerService } from '../../../common/services/spinner.service'
import { catchError, finalize, Observable, of, tap } from 'rxjs'
import { UserWelcomeResponse } from '../../../common/models/Login'
import { LoginFlowStateService } from '../../../common/store/state/login-flow/login-flow.state.service'
import { DeviceService } from '../../../common/services/device.service'
import { Message } from '../../../common/enums/message.enum'
import { NotificationService } from '../../../common/services/notification.service'
import { UserStateService } from '../../../common/store/state/user/user.state.service'
import { DiscoveryNextStep, DiscoveryResponse, LocationRequirement } from '../../../common/models/Discovery'
import { SsoAuthService, SSO_PENDING_KEY } from '../../../common/services/sso-auth.service'
import { SESSION_EXPIRED_KEY } from '../../../common/services/auth.service'
import { LocationBypassService } from '../../../common/services/location-bypass.service'
import { SentryService } from '../../../common/services/sentry.service'
import { getPendingLaunchId } from '../../../common/util/session-util'
import { EMAIL_PATTERN } from '../../../common/constants/app-constants'

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: false
})
export class LoginComponent extends BaseComponent implements OnInit {

  #authService = inject(AuthService)
  #loginFlowStateService = inject(LoginFlowStateService)
  #spinnerService = inject(SpinnerService)
  #deviceService = inject(DeviceService)
  #notificationService = inject(NotificationService)
  #userStateService = inject(UserStateService)
  #ssoAuthService = inject(SsoAuthService)
  #locationBypassService = inject(LocationBypassService)
  launchAuthRedirectService = inject(LaunchAuthRedirectService)
  #sentryService = inject(SentryService)
  loginForm: FormGroup
  showPassword: boolean
  invalidLogin: string
  sessionExpiredMessage: any[] = []

  discovered = false
  discoveryResult?: DiscoveryResponse
  #locationOptional = false
  ssoLoginEnabled = this.featureFlagService.isEnabled('enableSSOLogin')

  get showNativeLogin(): boolean {
    return this.discovered && this.discoveryResult?.nextStep === DiscoveryNextStep.NativeLogin
  }

  get showSso(): boolean {
    return this.discovered && this.discoveryResult?.nextStep === DiscoveryNextStep.EntraSso
  }

  chromeLocationDisabled = false
  firefoxLocationDisabled = false
  edgeLocationDisabled = false
  safariLocationDisabled = false

  // Shows browser-specific instructions for re-enabling location (the browser won't re-prompt
  // once denied). A plain method, not a computed, so it re-runs on every denial.
  errorComputation(): void {
    const browserType = this.#deviceService.browser()
    if (!this.#deviceService.locationDisabled()) {
      return
    } else if (!this.#deviceService.locationDenied()) {
      this.#notificationService.showError(Message.ErrorMessageLocationUnavailable)
    } else if (browserType === 'Chrome') {
      this.chromeLocationDisabled = true
    } else if (browserType === 'Firefox') {
      this.firefoxLocationDisabled = true
    } else if (browserType === 'Edge') {
      this.edgeLocationDisabled = true
    } else if (browserType === 'Safari') {
      this.safariLocationDisabled = true
    } else if (browserType !== '') {
      this.#notificationService.showError(Message.ErrorMessageLocationAccess)
    }
  }

  constructor() {
    super()
  }

  ngOnInit(): void {
    this.launchAuthRedirectService.captureFromQueryParams(this.route.snapshot.queryParamMap)

    if (sessionStorage.getItem(SESSION_EXPIRED_KEY)) {
      this.sessionExpiredMessage = [{ severity: 'warn', detail: Message.SessionExpiredInactivity }]
    }

    this.loginForm = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email, Validators.pattern(EMAIL_PATTERN)]),
      pass: new FormControl('', [Validators.required])
    })

    this.subscriptions.add(this.loginForm.get('email')?.valueChanges.subscribe(() => {
      this.loginForm.get('pass')?.reset()
      this.invalidLogin = ''

      this.discovered = false
      this.discoveryResult = undefined
      this.#locationOptional = false
    }))

    this.subscriptions.add(this.loginForm.get('pass')?.valueChanges.subscribe(() => {
      this.invalidLogin = ''
    }))

    if (this.#userStateService.getIsLoggedIn()) {
      this.#navigate()
    }
  }

  onSessionExpiredMessageClose(): void {
    sessionStorage.removeItem(SESSION_EXPIRED_KEY)
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword
  }

  onSubmit(): void {
    if (!this.ssoLoginEnabled) {
      this.login()
      return
    }
    if (!this.discovered) {
      this.runDiscovery()
    } else if (this.showNativeLogin) {
      this.login()
    }
  }

  runDiscovery(): void {
    this.invalidLogin = ''

    const emailControl = this.loginForm.get('email')
    if (emailControl?.invalid) {
      emailControl.markAsTouched()
      this.invalidLogin = 'Invalid Login Credentials.'
      return
    }
    this.#setSentryEmailTag(emailControl?.value)

    this.#spinnerService.setLoading(true, 'login')

    this.subscriptions.add(this.#authService.initAuthInformationAndDiscovery(emailControl?.value)
      .pipe(catchError((httpErrorResp) => {
        console.log(httpErrorResp)
        this.invalidLogin = httpErrorResp.error ? httpErrorResp.error : 'Invalid Login Credentials.'
        this.#spinnerService.setLoading(false, 'login')
        return of(null)
      })).subscribe((resp) => {
        if (resp) {
          this.#onDiscovered(resp)
        }
      }))
  }

  async #onDiscovered(resp: DiscoveryResponse): Promise<void> {
    if (!resp.validEmail ||
      (resp.nextStep !== DiscoveryNextStep.NativeLogin && resp.nextStep !== DiscoveryNextStep.EntraSso)) {
      this.invalidLogin = 'Invalid Login Credentials.'
      this.#spinnerService.setLoading(false, 'login')
      return
    }

    const allowed = await this.#resolveLocation(resp)
    this.#spinnerService.setLoading(false, 'login')
    if (!allowed) {
      return
    }

    this.discoveryResult = resp
    this.discovered = true
  }

  // Requests location per the discovery requirement. Required blocks (back to email entry) on
  // denial; Preferred/Optional prompt best-effort and proceed with default coordinates if denied.
  async #resolveLocation(resp: DiscoveryResponse): Promise<boolean> {
    const email = this.loginForm.get('email')?.value
    if (this.#locationBypassService.shouldBypassLocation(email)) {
      this.#locationOptional = true
      await this.#authService.initDevice(true)
      return true
    }

    const required = resp.locationRequirement === LocationRequirement.Required
    this.#locationOptional = !required

    await this.#authService.initDevice(false)
    if (!this.#deviceService.locationDisabled()) {
      return true
    }

    if (required) {
      this.loginForm.get('pass')?.reset()
      this.discovered = false
      this.discoveryResult = undefined
      this.errorComputation()
      this.invalidLogin = this.#deviceService.locationDenied()
        ? 'Location access is required to sign in. Enable location for this site in your browser, then try again.'
        : 'We could not determine your location. Check your connection and try again.'
      return false
    }

    await this.#authService.initDevice(true)
    return true
  }

  backToEmail(): void {
    this.invalidLogin = ''
    this.loginForm.get('pass')?.reset()
    this.discovered = false
    this.discoveryResult = undefined
    this.#locationOptional = false
  }

  async signInWithSso(): Promise<void> {
    this.invalidLogin = ''

    const email = this.loginForm.get('email')?.value
    const shouldBypassLocation = this.#locationOptional
    const launchId = getPendingLaunchId()
    this.#setSentryEmailTag(email)

    sessionStorage.setItem(SSO_PENDING_KEY, JSON.stringify({ email, byPassLocation: shouldBypassLocation, launchId }))

    this.#spinnerService.setLoading(true, 'login')
    try {
      await this.#ssoAuthService.signInRedirect(this.discoveryResult?.tenantId, launchId)
    } catch (error) {
      console.warn('SSO sign-in failed to start', error)
      sessionStorage.removeItem(SSO_PENDING_KEY)
      this.#spinnerService.setLoading(false, 'login')
    }
  }

  async login() {
    this.invalidLogin = ''

    // First check basic form validation (required fields and email format)
    if (!this.loginForm.valid) {
      this.invalidLogin = 'Invalid Login Credentials.'
      return
    }
    this.#setSentryEmailTag(this.loginForm.get('email')?.value)

    // Check password minimum length manually
    const passwordValue = this.loginForm.get('pass')?.value
    if (!passwordValue || passwordValue.length < 8) {
      this.invalidLogin = 'Invalid Login Credentials.'
      return
    }

    // If we reach here, password is >= 8 characters, proceed with API call
    this.#spinnerService.setLoading(true, 'login')

    if (this.ssoLoginEnabled) {
      const loginDetails = this.loginForm.value
      loginDetails.byPassLocation = this.#locationOptional
      await this.#runLogin(this.#authService.login(loginDetails))
      return
    }

    const email = this.loginForm.get('email')?.value
    const shouldBypassLocation = this.#locationBypassService.shouldBypassLocation(email)
    try {
      await this.#authService.initDevice(shouldBypassLocation)
    } catch (error) {
      console.warn('initDevice failed', error)
      this.#spinnerService.setLoading(false, 'login')
      return
    }
    if (!shouldBypassLocation && this.#deviceService.locationDisabled()) {
      this.errorComputation()
      this.#spinnerService.setLoading(false, 'login')
      return
    }
    const loginDetails = this.loginForm.value
    loginDetails.byPassLocation = shouldBypassLocation
    await this.#runLogin(this.#authService.initAuthInformationAndLogin(loginDetails))
  }

  async #runLogin(login$: Observable<UserWelcomeResponse>): Promise<void> {
    this.subscriptions.add(login$
      .pipe(tap(async () => {
        await this.#navigate()
      }), catchError((httpErrorResp) => {
        console.log(httpErrorResp)
        this.invalidLogin = httpErrorResp.error ? httpErrorResp.error : 'Invalid Login Credentials.'
        return of(httpErrorResp)
      }), finalize(() => {
        this.#spinnerService.setLoading(false, 'login')
      })).subscribe())
  }

  #setSentryEmailTag(email: string | undefined): void {
    const trimmedEmail = email?.trim().toLowerCase()
    if (trimmedEmail) {
      this.#sentryService.setTag('Email', trimmedEmail)
    }
  }

  // decide precedence based on login flow
  async #navigate() {
    const loginFlow = this.#loginFlowStateService.getLoginFlow()
    if (loginFlow.DoForcedPasswordReset && !this.showSso) {
      return this.router.navigate(['/reset-password/create-new'])
    } else if (loginFlow.Do2FaSetup) {
      return this.router.navigate(['/mfa/collection/email'])
    } else if (loginFlow.Do2FaCheck) {
      return this.router.navigate(['/mfa/selection'])
    } else if (loginFlow.DoSecurityQandA) {
      return this.router.navigate(['/security-qa'])
    } else if (this.featureFlagService.isLICProcessEnabled()) {
      const launchId = getPendingLaunchId();
      if (launchId) {
        return this.router.navigate(['auth/lic'], { queryParams: { launchId, ignoreLaunchContext: true } })
      }
    }

    return this.router.navigate([this.#authService.getHomeRoute()])
  }
}
